import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../../components/AppIcon';
import Button from '../../../../components/ui/Button';
import Input from '../../../../components/ui/Input';
import Select from '../../../../components/ui/Select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '../../../../components/ui/Dialog';
import { createTournament } from '@/api/TournamentManagement/tournamentApi';
import type { CreateTournamentInput } from '@/models/tournament';
import { TournamentFormat } from '@/models/tournament';

interface AddTournamentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: () => void;
}

interface FormData {
  name: string;
  venue: string;
  startDate: string;
  endDate: string;
  participants: string;
  format: string;
  description: string;
}

interface FormErrors {
  [key: string]: string;
}

const AddTournamentModal: React.FC<AddTournamentModalProps> = ({ isOpen, onClose, onCreated }) => {
  const { t } = useTranslation('tournament');
  const [formData, setFormData] = useState<FormData>({
    name: '',
    venue: '',
    startDate: '',
    endDate: '',
    participants: '',
    format: '0',
    description: '',
  });

  const formatOptions = [
    { value: '0', label: t('League') },
    { value: '1', label: t('Knockout') },
    { value: '2', label: t('Group Stage') },
  ];

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    setApiError(null);
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    setApiError(null);
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = t('Tournament name is required');
    }

    if (!formData.venue.trim()) {
      newErrors.venue = t('Venue is required');
    }

    if (!formData.startDate) {
      newErrors.startDate = t('Start date is required');
    }

    if (!formData.endDate) {
      newErrors.endDate = t('End date is required');
    }

    if (formData.startDate && formData.endDate && formData.startDate > formData.endDate) {
      newErrors.endDate = t('End date must be after start date');
    }

    if (!formData.participants || isNaN(parseInt(formData.participants)) || parseInt(formData.participants) < 2) {
      newErrors.participants = t('At least 2 participants required');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setApiError(null);

    try {
      const input: CreateTournamentInput = {
        name: formData.name.trim(),
        venue: formData.venue.trim(),
        startDate: formData.startDate,
        endDate: formData.endDate,
        participants: parseInt(formData.participants),
        format: parseInt(formData.format) as TournamentFormat,
        description: formData.description.trim() || undefined,
      };

      const result = await createTournament(input);

      if (result.errors.length > 0) {
        setApiError(result.errors[0]?.description || t('Failed to create tournament'));
        return;
      }

      setFormData({
        name: '',
        venue: '',
        startDate: '',
        endDate: '',
        participants: '',
        format: '0',
        description: '',
      });

      onCreated?.();
      onClose();
    } catch (error: any) {
      const msg = error?.response?.data?.errors?.[0]?.description
        || error?.message
        || t('Something went wrong. Please try again.');
      setApiError(msg);
      console.error('Error adding tournament:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Radix Dialog, not a hand-rolled `fixed inset-0` div. The old markup had no role="dialog", no
  // aria-modal, no focus trap, no focus restore, no Escape handler, and its close "X" was an
  // icon-only button with no accessible name. DialogContent supplies all of that (and a translated
  // sr-only name on its own close affordance).
  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => { if (!open && !isSubmitting) onClose(); }}
    >
      <DialogContent
        size="lg"
        // Every dismissal path is locked together while the request is in flight — X, backdrop and
        // Escape, matching the disabled Cancel button.
        onEscapeKeyDown={(e) => { if (isSubmitting) e.preventDefault(); }}
        onPointerDownOutside={(e) => { if (isSubmitting) e.preventDefault(); }}
        onInteractOutside={(e) => { if (isSubmitting) e.preventDefault(); }}
        data-testid="add-tournament-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center">
            <Icon name="Trophy" size={24} className="me-2 text-primary" />
            {t('Add New Tournament')}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          {/* DialogBody, not a bare child of DialogContent: direct children get zero padding. */}
          <DialogBody className="space-y-6">
            {/* API Error */}
            {apiError && (
              <div role="alert" className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                <div className="flex items-start">
                  <Icon name="AlertCircle" size={18} className="text-red-600 dark:text-red-400 me-2 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-red-800 dark:text-red-300">{apiError}</p>
                </div>
              </div>
            )}

            {/* Basic Info */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">{t('Basic Info')}</h3>

              <Input
                label={t('Tournament Name')}
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                error={errors.name}
                required
                placeholder={t('e.g., UAE National Football Championship 2026')}
                disabled={isSubmitting}
                data-testid="add-tournament-name-input"
              />

              <div className="space-y-2">
                <label htmlFor="add-tournament-description" className="block text-sm font-medium leading-none text-foreground">
                  {t('Description')}
                </label>
                <textarea
                  id="add-tournament-description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-3 py-2 bg-background border border-input rounded-md text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                  placeholder={t('Provide a brief description of the tournament...')}
                  disabled={isSubmitting}
                  data-testid="add-tournament-description-input"
                />
              </div>
            </div>

            <hr className="border-border" />

            {/* Configuration */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">{t('Configuration')}</h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Select
                  label={t('Format')}
                  value={formData.format}
                  onChange={(value: string) => handleSelectChange('format', value)}
                  options={formatOptions}
                  disabled={isSubmitting}
                  data-testid="add-tournament-format-select"
                />
                <Input
                  label={t('Number of Participants')}
                  type="number"
                  name="participants"
                  value={formData.participants}
                  onChange={handleInputChange}
                  error={errors.participants}
                  required
                  min="2"
                  placeholder={t('e.g., 8')}
                  disabled={isSubmitting}
                  data-testid="add-tournament-participants-input"
                />
              </div>
            </div>

            <hr className="border-border" />

            {/* Schedule & Venue */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">{t('Schedule & Venue')}</h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="min-w-0">
                  <Input
                    label={t('Start Date')}
                    type="date"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleInputChange}
                    error={errors.startDate}
                    required
                    disabled={isSubmitting}
                    data-testid="add-tournament-start-date"
                  />
                </div>
                <div className="min-w-0">
                  <Input
                    label={t('End Date')}
                    type="date"
                    name="endDate"
                    value={formData.endDate}
                    onChange={handleInputChange}
                    error={errors.endDate}
                    required
                    disabled={isSubmitting}
                    data-testid="add-tournament-end-date"
                  />
                </div>
              </div>

              <Input
                label={t('Venue')}
                name="venue"
                value={formData.venue}
                onChange={handleInputChange}
                error={errors.venue}
                required
                placeholder={t('e.g., Dubai Sports City')}
                disabled={isSubmitting}
                data-testid="add-tournament-venue-input"
              />
            </div>
          </DialogBody>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
              data-testid="add-tournament-cancel-btn"
            >
              {t('Cancel')}
            </Button>
            <Button
              type="submit"
              loading={isSubmitting}
              disabled={isSubmitting}
              data-testid="add-tournament-submit-btn"
            >
              {t('Create Tournament')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddTournamentModal;
